import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { PageHeader, Button, Card, Modal, FormField, Input, Select, Textarea, formatCurrency, Badge, EditableText } from '../ui';
import { Plus, ShoppingCart, Trash2, Package, Tag, Edit2 } from 'lucide-react';

const INITIAL_PRODUCTS = [
  { id: 'prod1', name: 'UC Elites Jersey', description: 'Official game jersey with player number and name. Black with gold trim.', price: 65, category: 'Uniforms', sizes: ['YS','YM','YL','S','M','L','XL','2XL'], inStock: true, featured: true, image: '👕', colors: ['Black/Gold'] },
  { id: 'prod2', name: 'Training Shorts', description: 'Lightweight performance shorts with UC Elites crest on left leg.', price: 35, category: 'Uniforms', sizes: ['YS','YM','YL','S','M','L','XL'], inStock: true, featured: false, image: '🩳', colors: ['Black','Gold'] },
  { id: 'prod3', name: 'UC Elites Hoodie', description: 'Premium fleece pullover hoodie. Gold embroidered crest on chest.', price: 55, category: 'Apparel', sizes: ['YS','YM','YL','S','M','L','XL','2XL'], inStock: true, featured: true, image: '🧥', colors: ['Black'] },
  { id: 'prod4', name: 'Practice T-Shirt', description: 'Moisture-wicking performance tee for training sessions.', price: 28, category: 'Apparel', sizes: ['YS','YM','YL','S','M','L','XL'], inStock: true, featured: false, image: '👕', colors: ['Black','White','Gold'] },
  { id: 'prod5', name: 'Backpack / Gear Bag', description: 'Large capacity team backpack with ball compartment and UC Elites branding.', price: 75, category: 'Equipment', sizes: ['One Size'], inStock: true, featured: true, image: '🎒', colors: ['Black/Gold'] },
  { id: 'prod6', name: 'Water Bottle (32oz)', description: 'Stainless steel insulated bottle with UC Elites shield logo.', price: 30, category: 'Accessories', sizes: ['One Size'], inStock: false, featured: false, image: '🍶', colors: ['Black','Gold'] },
  { id: 'prod7', name: 'UC Elites Cap', description: 'Structured snapback cap with embroidered shield logo.', price: 32, category: 'Accessories', sizes: ['One Size'], inStock: true, featured: false, image: '🧢', colors: ['Black/Gold','All Black'] },
  { id: 'prod8', name: 'Compression Socks', description: 'Performance knee-high socks. Black with gold stripes.', price: 18, category: 'Accessories', sizes: ['S/M','L/XL'], inStock: true, featured: false, image: '🧦', colors: ['Black/Gold'] },
];

const CATEGORIES = ['All', 'Uniforms', 'Apparel', 'Equipment', 'Accessories'];

export default function Store() {
  const { isAdmin } = useAuth();
  const [products, setProducts] = useState(INITIAL_PRODUCTS);
  const [cart, setCart] = useState([]);
  const [category, setCategory] = useState('All');
  const [showCart, setShowCart] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutDone, setCheckoutDone] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [form, setForm] = useState({ name: '', description: '', price: '', category: 'Apparel', sizes: 'S,M,L,XL', colors: 'Black/Gold', inStock: true, featured: false, image: '👕' });

  const filtered = category === 'All' ? products : products.filter(p => p.category === category);
  const cartTotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  const addToCart = (product, size, color) => {
    const key = `${product.id}-${size}-${color}`;
    setCart(prev => {
      const existing = prev.find(i => i.key === key);
      if (existing) return prev.map(i => i.key === key ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { key, product: product.name, productId: product.id, size, color, price: product.price, qty: 1, image: product.image }];
    });
    setSelectedProduct(null);
  };

  const updateQty = (key, delta) => {
    setCart(prev => prev.map(i => i.key === key ? { ...i, qty: Math.max(0, i.qty + delta) } : i).filter(i => i.qty > 0));
  };

  const submitAdd = (e) => {
    e.preventDefault();
    setProducts(prev => [...prev, { ...form, id: 'prod' + Date.now(), price: Number(form.price), sizes: form.sizes.split(',').map(s => s.trim()), colors: form.colors.split(',').map(c => c.trim()) }]);
    setShowAdd(false);
  };

  const updateProduct = (id, key, val) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, [key]: val } : p));
  };

  const deleteProduct = (id) => setProducts(prev => prev.filter(p => p.id !== id));

  const fakeCheckout = () => {
    setTimeout(() => { setCheckoutDone(true); setCart([]); }, 1500);
  };

  return (
    <div>
      <PageHeader title="Team Store" subtitle={`${products.length} items available · Official UC Elites merchandise`}
        actions={<>
          {isAdmin && <Button variant="dark" onClick={() => setShowAdd(true)}><Plus size={14} /> Add product</Button>}
          <Button variant="primary" onClick={() => setShowCart(true)} style={{ position: 'relative' }}>
            <ShoppingCart size={15} /> Cart
            {cartCount > 0 && <span style={{ background: '#0a0a0a', color: 'var(--gold-bright)', fontSize: 10, fontWeight: 800, padding: '1px 5px', borderRadius: 10, marginLeft: 2 }}>{cartCount}</span>}
          </Button>
        </>}
      />

      {/* Category filters */}
      <div style={{ display: 'flex', gap: 6, marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {CATEGORIES.map(cat => (
          <button key={cat} onClick={() => setCategory(cat)}
            style={{ padding: '6px 16px', borderRadius: 20, border: category === cat ? 'none' : '1px solid var(--border-dark)', background: category === cat ? 'linear-gradient(135deg, var(--gold-deep), var(--gold-bright))' : 'var(--surface-0)', color: category === cat ? '#0a0a0a' : 'var(--text-secondary)', fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s' }}>
            {cat}
          </button>
        ))}
      </div>

      {/* Featured banner */}
      {category === 'All' && (
        <div style={{ background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%)', borderRadius: 12, padding: '1.5rem 2rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '2rem', border: '1px solid rgba(230,172,0,0.2)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, var(--gold-deep), var(--gold-bright), var(--gold-deep))' }} />
          <div style={{ fontSize: 60 }}>👕</div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.18em', color: 'var(--gold-muted)', textTransform: 'uppercase', fontFamily: 'var(--font-condensed)', marginBottom: 6 }}>⭐ Featured item</div>
            <h3 style={{ fontSize: 22, fontWeight: 900, fontFamily: 'var(--font-condensed)', letterSpacing: '0.06em', color: 'var(--gold-bright)', marginBottom: 4 }}>Official UC Elites Jersey</h3>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, marginBottom: 12 }}>Game-ready jersey with number and name. Black with gold trim.</p>
            <button onClick={() => setSelectedProduct(products[0])}
              style={{ background: 'linear-gradient(135deg, var(--gold-deep), var(--gold-bright))', color: '#0a0a0a', border: 'none', padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
              Order Now — {formatCurrency(65)}
            </button>
          </div>
        </div>
      )}

      {/* Product grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem' }}>
        {filtered.map(product => (
          <div key={product.id} style={{ background: 'var(--surface-0)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', transition: 'all 0.18s', position: 'relative' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold-border)'; e.currentTarget.style.boxShadow = 'var(--shadow-gold)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = ''; }}>
            {/* Product image area */}
            <div style={{ background: 'linear-gradient(135deg, #0a0a0a, #1a1a1a)', padding: '2rem', textAlign: 'center', fontSize: 56, position: 'relative' }}>
              {product.featured && (
                <div style={{ position: 'absolute', top: 8, left: 8, background: 'linear-gradient(135deg, var(--gold-deep), var(--gold-bright))', color: '#0a0a0a', fontSize: 9, fontWeight: 800, padding: '3px 8px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Featured</div>
              )}
              {!product.inStock && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ background: '#1a1a1a', color: 'var(--gold-muted)', fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20, border: '1px solid rgba(230,172,0,0.2)' }}>OUT OF STOCK</span>
                </div>
              )}
              {product.image}
            </div>

            <div style={{ padding: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                <EditableText value={product.name} isAdmin={isAdmin} onSave={v => updateProduct(product.id, 'name', v)}
                  style={{ fontSize: 15, fontWeight: 800, fontFamily: 'var(--font-condensed)', color: 'var(--text-primary)', letterSpacing: '0.03em' }} />
                {isAdmin && <button onClick={() => deleteProduct(product.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e57373', padding: 4, display: 'flex' }}><Trash2 size={13} /></button>}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                <span style={{ background: 'var(--gold-pale)', color: 'var(--gold-muted)', padding: '1px 7px', borderRadius: 10, border: '1px solid var(--gold-border)' }}>{product.category}</span>
              </div>
              <EditableText value={product.description} isAdmin={isAdmin} onSave={v => updateProduct(product.id, 'description', v)}
                tag="p" style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 10 }} />

              <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
                {product.sizes?.slice(0,5).map(s => (
                  <span key={s} style={{ fontSize: 10, fontWeight: 700, border: '1px solid var(--border)', borderRadius: 4, padding: '1px 6px', color: 'var(--text-muted)' }}>{s}</span>
                ))}
                {product.sizes?.length > 5 && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>+{product.sizes.length - 5}</span>}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 20, fontWeight: 900, fontFamily: 'var(--font-condensed)', color: 'var(--gold-deep)', letterSpacing: '0.02em' }}>{formatCurrency(product.price)}</span>
                <button disabled={!product.inStock} onClick={() => { setSelectedProduct(product); setSelectedSize(product.sizes[0]); setSelectedColor(product.colors[0]); }}
                  style={{ background: product.inStock ? 'linear-gradient(135deg, var(--gold-deep), var(--gold-bright))' : 'var(--surface-2)', color: product.inStock ? '#0a0a0a' : 'var(--text-muted)', border: 'none', padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: product.inStock ? 'pointer' : 'not-allowed', fontFamily: 'var(--font-sans)', transition: 'all 0.15s' }}>
                  <ShoppingCart size={12} style={{ display: 'inline', marginRight: 5 }} />
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Size/color selector modal */}
      <Modal open={!!selectedProduct} onClose={() => setSelectedProduct(null)} title={`Order — ${selectedProduct?.name}`} width={420}>
        {selectedProduct && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: 'linear-gradient(135deg, #0a0a0a, #1a1a1a)', borderRadius: 10, padding: '1.5rem', textAlign: 'center', fontSize: 64 }}>
              {selectedProduct.image}
            </div>
            <div>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>{selectedProduct.description}</p>
              <p style={{ fontSize: 22, fontWeight: 900, fontFamily: 'var(--font-condensed)', color: 'var(--gold-deep)' }}>{formatCurrency(selectedProduct.price)}</p>
            </div>
            <FormField label="Size">
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {selectedProduct.sizes?.map(s => (
                  <button key={s} onClick={() => setSelectedSize(s)}
                    style={{ padding: '6px 12px', border: selectedSize === s ? 'none' : '1px solid var(--border-dark)', borderRadius: 6, background: selectedSize === s ? 'linear-gradient(135deg, var(--gold-deep), var(--gold-bright))' : 'var(--surface-0)', color: selectedSize === s ? '#0a0a0a' : 'var(--text-primary)', fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all 0.1s' }}>
                    {s}
                  </button>
                ))}
              </div>
            </FormField>
            {selectedProduct.colors?.length > 1 && (
              <FormField label="Color">
                <Select value={selectedColor} onChange={e => setSelectedColor(e.target.value)}>
                  {selectedProduct.colors.map(c => <option key={c}>{c}</option>)}
                </Select>
              </FormField>
            )}
            <Button variant="primary" style={{ width: '100%', justifyContent: 'center', padding: '11px' }} onClick={() => addToCart(selectedProduct, selectedSize, selectedColor || selectedProduct.colors?.[0])}>
              <ShoppingCart size={15} /> Add to Cart
            </Button>
          </div>
        )}
      </Modal>

      {/* Cart modal */}
      <Modal open={showCart} onClose={() => setShowCart(false)} title="Shopping Cart" width={480}>
        {cart.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
            <ShoppingCart size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
            <p style={{ fontWeight: 700 }}>Your cart is empty</p>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1, marginBottom: '1rem' }}>
              {cart.map(item => (
                <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 28 }}>{products.find(p => p.id === item.productId)?.image}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{item.product}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{item.size} · {item.color}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--surface-2)', borderRadius: 6, padding: '3px 6px' }}>
                      <button onClick={() => updateQty(item.key, -1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 800, color: 'var(--text-primary)', fontSize: 16, lineHeight: 1, padding: '0 2px' }}>−</button>
                      <span style={{ fontSize: 14, fontWeight: 700, minWidth: 16, textAlign: 'center' }}>{item.qty}</span>
                      <button onClick={() => updateQty(item.key, 1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 800, color: 'var(--text-primary)', fontSize: 16, lineHeight: 1, padding: '0 2px' }}>+</button>
                    </div>
                    <span style={{ fontWeight: 800, fontFamily: 'var(--font-condensed)', fontSize: 16, minWidth: 60, textAlign: 'right', color: 'var(--gold-deep)' }}>{formatCurrency(item.price * item.qty)}</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderTop: '2px solid var(--border-dark)', marginBottom: 16 }}>
              <span style={{ fontWeight: 800, fontSize: 16, fontFamily: 'var(--font-condensed)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Total</span>
              <span style={{ fontWeight: 900, fontSize: 22, fontFamily: 'var(--font-condensed)', color: 'var(--gold-deep)' }}>{formatCurrency(cartTotal)}</span>
            </div>
            <Button variant="primary" style={{ width: '100%', justifyContent: 'center', padding: '12px' }} onClick={() => { setShowCart(false); setShowCheckout(true); setCheckoutDone(false); }}>
              Proceed to Checkout →
            </Button>
          </div>
        )}
      </Modal>

      {/* Checkout modal */}
      <Modal open={showCheckout} onClose={() => { setShowCheckout(false); setCheckoutDone(false); }} title="Checkout" width={460}>
        {!checkoutDone ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ background: 'var(--gold-pale)', border: '1px solid var(--gold-border)', borderRadius: 10, padding: '10px 14px', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>Order total</span>
              <span style={{ fontWeight: 900, fontFamily: 'var(--font-condensed)', fontSize: 20, color: 'var(--gold-deep)' }}>{formatCurrency(cartTotal)}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormField label="First name"><Input placeholder="First" /></FormField>
              <FormField label="Last name"><Input placeholder="Last" /></FormField>
            </div>
            <FormField label="Email"><Input type="email" placeholder="you@example.com" /></FormField>
            <FormField label="Shipping address"><Input placeholder="123 Main St" /></FormField>
            <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
            <div style={{ background: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 12, color: '#6c63ff', fontWeight: 700, marginBottom: 8 }}>Stripe Payment</div>
              <FormField label="Card number"><Input value="4242 4242 4242 4242" readOnly style={{ background: 'var(--surface-1)' }} /></FormField>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                <FormField label="Expiry"><Input value="12/27" readOnly style={{ background: 'var(--surface-1)' }} /></FormField>
                <FormField label="CVC"><Input value="123" readOnly style={{ background: 'var(--surface-1)' }} /></FormField>
              </div>
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>🔒 Payments processed securely via Stripe</p>
            <Button variant="primary" style={{ width: '100%', justifyContent: 'center', padding: '12px' }} onClick={fakeCheckout}>
              Pay {formatCurrency(cartTotal)}
            </Button>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🏆</div>
            <h3 style={{ fontSize: 22, fontWeight: 900, fontFamily: 'var(--font-condensed)', color: 'var(--gold-deep)', marginBottom: 8, letterSpacing: '0.04em' }}>ORDER CONFIRMED!</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>Your UC Elites gear is on its way. You'll receive an email confirmation shortly.</p>
            <Button variant="primary" onClick={() => setShowCheckout(false)}>Back to store</Button>
          </div>
        )}
      </Modal>

      {/* Add product modal */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add product" width={520}>
        <form onSubmit={submitAdd} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="Product name" required><Input value={form.name} onChange={e => setForm(p=>({...p,name:e.target.value}))} required placeholder="e.g. Training Jersey" /></FormField>
            <FormField label="Category">
              <Select value={form.category} onChange={e => setForm(p=>({...p,category:e.target.value}))}>
                {['Uniforms','Apparel','Equipment','Accessories'].map(c => <option key={c}>{c}</option>)}
              </Select>
            </FormField>
            <FormField label="Price ($)" required><Input type="number" value={form.price} onChange={e => setForm(p=>({...p,price:e.target.value}))} required min="0" placeholder="45" /></FormField>
            <FormField label="Emoji icon"><Input value={form.image} onChange={e => setForm(p=>({...p,image:e.target.value}))} placeholder="👕" /></FormField>
            <FormField label="Sizes (comma separated)"><Input value={form.sizes} onChange={e => setForm(p=>({...p,sizes:e.target.value}))} placeholder="S,M,L,XL" /></FormField>
            <FormField label="Colors (comma separated)"><Input value={form.colors} onChange={e => setForm(p=>({...p,colors:e.target.value}))} placeholder="Black/Gold" /></FormField>
          </div>
          <FormField label="Description"><Textarea value={form.description} onChange={e => setForm(p=>({...p,description:e.target.value}))} rows={2} placeholder="Product description…" /></FormField>
          <div style={{ display: 'flex', gap: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={form.inStock} onChange={e => setForm(p=>({...p,inStock:e.target.checked}))} /> In stock
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={form.featured} onChange={e => setForm(p=>({...p,featured:e.target.checked}))} /> Featured
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button type="button" variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button type="submit" variant="primary">Add product</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
